/**
 * Single source of truth for all site and antenna exceptions in GSOC.
 *
 * - Non-operational ground stations: excluded from health UI views by default.
 * - Capability exceptions: per-antenna status suppression (only matches when
 *   current status equals the accepted status — transitions always surface).
 * - All exceptions support optional expiry — after expiresAt, the real status resurfaces.
 */

import { CapabilityStatus } from '../types/capabilityStatusTypes';

/** Use as expiresAt for exceptions with no planned resolution. */
export const INDEFINITE = '9999-12-31';

// ─── Non-Operational Ground Stations ─────────────────────────────────────────

interface SiteException {
  readonly expiresAt: string;
}

/** Ground stations excluded from all GSOC views. */
export const NON_OPERATIONAL_GROUND_STATIONS: Readonly<Record<string, SiteException>> = {
  'Bahrain 1': { expiresAt: INDEFINITE },
  'Seoul 1': { expiresAt: INDEFINITE },
};

/** Returns false if the ground station is non-operational and should be excluded. */
export function isOperationalGroundStation(groundStationName: string, now: Date = new Date()): boolean {
  const exception = NON_OPERATIONAL_GROUND_STATIONS[groundStationName];
  if (exception === undefined) return true;
  return now >= new Date(exception.expiresAt);
}

// ─── Capability Exceptions ──────────────────────────────────────────────────

interface CapabilityException {
  readonly acceptedStatus: CapabilityStatus;
  readonly expiresAt: string;
}

/**
 * XBandManagedDemodDecodeLeftCapabilityLF / XBandManagedDemodDecodeRightCapabilityLF:
 *   Managed SDR work is not planned. These report DISABLED across all antennas.
 *
 * WidebandRHCPDownlinkStream{1-5}Capability / WidebandLHCPDownlinkStream{1-5}Capability
 *   (SCC and PUQ only): Network limitations keep wideband downlink in INTERNAL_TESTING.
 */

const MANAGED_DEMOD_DECODE_EXCEPTIONS: Record<string, CapabilityException> = {
  XBandManagedDemodDecodeLeftCapabilityLF: { acceptedStatus: CapabilityStatus.DISABLED, expiresAt: INDEFINITE },
  XBandManagedDemodDecodeRightCapabilityLF: { acceptedStatus: CapabilityStatus.DISABLED, expiresAt: INDEFINITE },
};

const WIDEBAND_DOWNLINK_EXCEPTIONS: Record<string, CapabilityException> = {
  WidebandRHCPDownlinkStream1Capability: { acceptedStatus: CapabilityStatus.INTERNAL_TESTING, expiresAt: INDEFINITE },
  WidebandRHCPDownlinkStream2Capability: { acceptedStatus: CapabilityStatus.INTERNAL_TESTING, expiresAt: INDEFINITE },
  WidebandRHCPDownlinkStream3Capability: { acceptedStatus: CapabilityStatus.INTERNAL_TESTING, expiresAt: INDEFINITE },
  WidebandRHCPDownlinkStream4Capability: { acceptedStatus: CapabilityStatus.INTERNAL_TESTING, expiresAt: INDEFINITE },
  WidebandRHCPDownlinkStream5Capability: { acceptedStatus: CapabilityStatus.INTERNAL_TESTING, expiresAt: INDEFINITE },
  WidebandLHCPDownlinkStream1Capability: { acceptedStatus: CapabilityStatus.INTERNAL_TESTING, expiresAt: INDEFINITE },
  WidebandLHCPDownlinkStream2Capability: { acceptedStatus: CapabilityStatus.INTERNAL_TESTING, expiresAt: INDEFINITE },
  WidebandLHCPDownlinkStream3Capability: { acceptedStatus: CapabilityStatus.INTERNAL_TESTING, expiresAt: INDEFINITE },
  WidebandLHCPDownlinkStream4Capability: { acceptedStatus: CapabilityStatus.INTERNAL_TESTING, expiresAt: INDEFINITE },
  WidebandLHCPDownlinkStream5Capability: { acceptedStatus: CapabilityStatus.INTERNAL_TESTING, expiresAt: INDEFINITE },
};

export const KNOWN_CAPABILITY_EXCEPTIONS: Readonly<Record<string, Readonly<Record<string, CapabilityException>>>> = {
  'arn52-ant1': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'arn52-ant2': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'cmh51-ant1': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'cmh51-ant2': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'cpt60-ant1': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'cpt60-ant2': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'dbo1-ant1': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'dbo1-ant2': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'dub4-ant1': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'dub4-ant2': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'hnl1-ant1': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'hnl1-ant2': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'pdx59-ant1': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'pdx59-ant2': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'sin61-ant1': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'sin61-ant2': MANAGED_DEMOD_DECODE_EXCEPTIONS,
  'puq1-ant1': { ...MANAGED_DEMOD_DECODE_EXCEPTIONS, ...WIDEBAND_DOWNLINK_EXCEPTIONS },
  'puq1-ant2': { ...MANAGED_DEMOD_DECODE_EXCEPTIONS, ...WIDEBAND_DOWNLINK_EXCEPTIONS },
  'scc1-ant1': { ...MANAGED_DEMOD_DECODE_EXCEPTIONS, ...WIDEBAND_DOWNLINK_EXCEPTIONS },
  'scc1-ant2': { ...MANAGED_DEMOD_DECODE_EXCEPTIONS, ...WIDEBAND_DOWNLINK_EXCEPTIONS },
};

/**
 * Returns whether a capability is a known exception for the given antenna.
 * When `currentStatus` is provided, only matches if the status equals the accepted status.
 * When omitted, matches if any non-expired exception exists.
 */
export function isKnownCapabilityException(
  antennaId: string,
  capabilityName: string,
  currentStatus?: CapabilityStatus,
  now: Date = new Date(),
): boolean {
  const exception = KNOWN_CAPABILITY_EXCEPTIONS[antennaId]?.[capabilityName];
  if (exception === undefined) return false;
  if (now >= new Date(exception.expiresAt)) return false;
  if (currentStatus !== undefined && exception.acceptedStatus !== currentStatus) return false;
  return true;
}
